import {useCallback, useEffect, useState} from 'react';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {useDebounce} from '../../../../../srcCommon/helpers/hookHelpers';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {buildCancelTokenSource, CancelError, CancelTokenSource} from '../../../../helpers/asyncCancelHelpers';
import {FilterDropdownResults, FilterDropdownSource} from './filterDropdownSource';

/*
 * Types.
 */

interface FilterDropdownSourceSearchHandlers<T> {
  asyncStatus: AsyncStatusesEnum;
  // Perform a search using the searchQuery passed in. Sets the
  // results upon completion.
  performSearch: Handler;
  // Attempt to load more results.
  loadMore: Handler;
  results?: FilterDropdownResults<T>;
}

/*
 * Constants.
 */

// Grace period avoids the flicker of the loading screen when fetching
// results. We wait this many ms before changing the async status to loading.
const defaultGracePeriod = 300;

/*
 * Hook.
 */

export const useFilterDropdownSource = <T>(
  source: FilterDropdownSource<T>,
  searchQuery?: string,
): FilterDropdownSourceSearchHandlers<T> => {
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);
  const [results, setResults] = useState<FilterDropdownResults<T>>();
  const [searchCancelTokenSource, setSearchCancelTokenSource] = useState<CancelTokenSource | undefined>();
  // Use a debounce to avoid flickering when setting the loading status.
  const updateLoadingStatusDebounced = useDebounce(
    () => setAsyncStatus(AsyncStatusesEnum.LOADING),
    defaultGracePeriod,
  );

  /*
   * Search logic.
   */

  const clearGraceTimeout = () => {
    updateLoadingStatusDebounced.cancel();
  };

  const startGracePeriod = () => {
    // If there's already one, clear it.
    clearGraceTimeout();

    // Start a new one.
    updateLoadingStatusDebounced();
  };

  const maybeCancelRequest = useCallback(
    () => searchCancelTokenSource && searchCancelTokenSource.cancel(),
    [searchCancelTokenSource],
  );

  const performSearchAsync = async (query: string) => {
    // Cancel any existing request.
    maybeCancelRequest();

    try {
      // Perform the search.
      startGracePeriod();
      const newSearchCancelTokenSource = buildCancelTokenSource();
      setSearchCancelTokenSource(newSearchCancelTokenSource);
      const newResults = await source.searchAsync(query, newSearchCancelTokenSource.token);

      clearGraceTimeout();
      setSearchCancelTokenSource(undefined);

      // Update the state with the results.
      setAsyncStatus(AsyncStatusesEnum.LOADED);
      setResults(newResults);
    } catch (error) {
      // Ignore expected HTTP Cancel errors
      if (error instanceof CancelError) {
        return;
      }

      clearGraceTimeout();
      setSearchCancelTokenSource(undefined);
      reportToBugsnag(error);

      // Otherwise, display a failed status.
      setAsyncStatus(AsyncStatusesEnum.FAILED);
    }
  };

  const performSearchFromPropsAsync = () => {
    const query = searchQuery ?? '';
    performSearchAsync(query);
  };

  const maybeLoadMoreAsync = async () => {
    // If there's already a request in progress, of if we can't load more, nothing to do.
    if (searchCancelTokenSource || !results || !results.canLoadMore || !results.loadMoreAsync) {
      return;
    }

    // Otherwise, try to load more results.
    try {
      const newSearchCancelTokenSource = buildCancelTokenSource();
      setSearchCancelTokenSource(newSearchCancelTokenSource);
      const newResults = await results.loadMoreAsync(newSearchCancelTokenSource.token);

      // Update the state with the results.
      setSearchCancelTokenSource(undefined);
      setResults(newResults);
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        return;
      }

      // Report non-cancel errors to bugsnag.
      reportToBugsnag(error);

      // Otherwise, display a failed status.
      setSearchCancelTokenSource(undefined);
      setAsyncStatus(AsyncStatusesEnum.FAILED);
    }
  };

  // Cleanup.
  useEffect(() => () => maybeCancelRequest(), [maybeCancelRequest]);

  return {asyncStatus, performSearch: performSearchFromPropsAsync, loadMore: maybeLoadMoreAsync, results};
};
